import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Play,
  CheckCircle2,
  Newspaper,
  Radar,
  BarChart3,
  Headset,
  Wrench,
  Smartphone,
  Search,
  Home,
  Users,
  BookOpen,
  CalendarDays,
  MapPin,
  Sparkles,
  Clock,
  Circle,
  Store,
  ShieldCheck,
  Target,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Reveal } from "@/components/Shared";
import VideoModal from "@/components/VideoModal";

const MODULE_NAV_ICONS = [Newspaper, Radar, BarChart3, Headset, Wrench, Smartphone];

import { MOCKS, MODULE_COLORS, ModuleVisual, StoreMock } from "@/components/ModuleMocks";

const scrollToModule = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -150 });
  else el.scrollIntoView({ behavior: "smooth" });
};

const FeaturesPage = () => {
  const { t } = useLanguage();
  usePageTitle(t.nav.features);
  const f = t.featuresPage;
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <main data-testid="features-page" className="pt-[76px]">
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50/80 via-white to-white py-16 lg:py-24">
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-60 [mask-image:radial-gradient(ellipse_at_top,black_20%,transparent_70%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-[1.05fr_1fr] lg:px-8">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="features-eyebrow"
              className="inline-block max-w-md text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-500 sm:text-xs"
            >
              {f.eyebrow}
            </motion.p>
            <h1
              data-testid="features-title"
              className="mt-5 font-display text-4xl font-extrabold leading-[1.06] tracking-tight text-slate-900 sm:text-5xl lg:text-[3.4rem]"
            >
              {f.titleLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i >= f.blueFrom ? "text-blue-600" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="features-subtitle"
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              {f.subtitle}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/demo"
                data-testid="features-demo-button"
                className="group flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all duration-300 hover:bg-blue-700"
              >
                {f.ctaDemo}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <button
                data-testid="features-video-button"
                onClick={() => setVideoOpen(true)}
                className="group flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-bold text-slate-700 transition-colors duration-300 hover:border-slate-300 hover:bg-slate-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 transition-transform duration-300 group-hover:scale-110">
                  <Play className="ml-0.5 h-3 w-3 fill-white text-white" />
                </span>
                {f.ctaVideo}
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 48, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            data-testid="features-hero-mockups"
          >
            <p className="font-hand absolute -top-20 left-0 z-10 max-w-xs rotate-[-3deg] text-xl font-semibold text-amber-500 lg:text-2xl">
              {f.annotation}
            </p>
            <div className="relative">
              <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_40px_80px_-20px_rgba(10,15,29,0.35)]">
                <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-2">
                  <span className="h-2 w-2 rounded-full bg-rose-400" />
                  <span className="h-2 w-2 rounded-full bg-amber-400" />
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="ml-2 flex-1 rounded-md border border-slate-100 bg-white px-2 py-0.5 font-mono-orbit text-[9px] text-slate-400">
                    app.orbit.fr — vue manager
                  </span>
                </div>
                <img src="/assets/orbit-dashboard.png" alt="Interface Orbit — vue manager" className="block w-full" />
              </div>
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-10 -right-4 w-36 rotate-3 lg:-right-8 lg:w-44"
              >
                <StoreMock />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <nav
        data-testid="modules-nav"
        className="sticky top-[76px] z-40 border-b border-slate-100 bg-white/85 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-5 py-3 lg:justify-center lg:px-8">
          {f.modulesNav.map((mod, i) => {
            const Icon = MODULE_NAV_ICONS[i];
            return (
              <button
                key={mod.id}
                data-testid={`module-nav-${mod.id}`}
                onClick={() => scrollToModule(mod.id)}
                className="group flex shrink-0 items-center gap-2.5 rounded-xl px-3.5 py-2 transition-colors duration-200 hover:bg-slate-50"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition-colors group-hover:bg-blue-100">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-left leading-tight">
                  <span className="block text-xs font-bold text-slate-900">{mod.label}</span>
                  <span className="block text-[10px] font-medium text-slate-400">{mod.action}</span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {f.modules.map((mod, i) => {
        const colors = MODULE_COLORS[mod.id];
        const Mock = MOCKS[mod.id];
        return (
          <section
            key={mod.id}
            id={mod.id}
            data-testid={`module-section-${mod.id}`}
            className={`py-20 lg:py-28 ${i % 2 === 1 ? "bg-slate-50/70" : "bg-white"}`}
          >
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
              <Reveal>
                <div className="relative">
                  <p
                    data-testid={`module-annotation-${mod.id}`}
                    className="font-hand mb-4 max-w-[240px] rotate-[-2deg] text-lg font-semibold text-amber-500 lg:absolute lg:-top-12 lg:right-0 lg:mb-0 lg:text-xl"
                  >
                    {mod.annotation}
                  </p>
                  <span className="font-mono-orbit text-sm font-bold text-emerald-500">{mod.chapter}</span>
                  <h2 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                    {mod.titleMain}
                  </h2>
                  <p className={`mt-1 font-display text-lg font-semibold italic sm:text-xl ${colors.text}`}>
                    {mod.titleSub}
                  </p>
                  <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">{mod.subtitle}</p>
                  <ul className="mt-7 space-y-3">
                    {mod.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-sm font-medium text-slate-700">
                        <CheckCircle2 className={`mt-0.5 h-5 w-5 shrink-0 ${colors.check}`} />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/fonctionnalites/${mod.id}`}
                    data-testid={`module-cta-${mod.id}`}
                    className="group mt-8 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-blue-700"
                  >
                    {mod.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div data-testid={`module-mock-${mod.id}`} className={mod.id === "magasin" ? "flex justify-center" : ""}>
                  <ModuleVisual id={mod.id} />
                </div>
              </Reveal>
            </div>
          </section>
        );
      })}

      <section data-testid="features-final-cta" className="relative overflow-hidden bg-[#0A0F1D] py-20 lg:py-28">
        <div className="pointer-events-none absolute inset-0 bg-grid-navy" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="text-center">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              {f.finalCta.title}
              <span className="block text-emerald-400">{f.finalCta.title2}</span>
            </h2>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              {f.finalCta.badges.map((badge, i) => {
                const Icon = [Store, BarChart3, Headset][i];
                return (
                  <span
                    key={badge.tag}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-5 py-3.5 backdrop-blur"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15">
                      <Icon className="h-5 w-5 text-emerald-400" />
                    </span>
                    <span className="text-left leading-tight">
                      <span className="block text-[10px] font-extrabold uppercase tracking-widest text-emerald-400">
                        {badge.tag}
                      </span>
                      <span className="block text-sm font-bold text-white">{badge.label}</span>
                    </span>
                  </span>
                );
              })}
            </div>
            <Link
              to="/demo"
              data-testid="features-final-cta-button"
              className="group mt-10 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-blue-500"
            >
              {f.finalCta.button}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <VideoModal
        open={videoOpen}
        onOpenChange={setVideoOpen}
        title={t.hero.videoTitle}
        testid="features-video-modal"
      />
    </main>
  );
};

export default FeaturesPage;
